import React, { useEffect, useState, useRef } from "react";
import api from "../services/api";
import { 
  MessageSquare, Send, Video, Plus, X, 
  CheckCircle2, User, HelpCircle, VideoOff, ArrowRight
} from "lucide-react";
import { jwtDecode } from "jwt-decode";
import toast from "react-hot-toast";
import { getToken } from "../services/auth";


export default function DoubtPortal({ isEmbedded }) {
  const [role, setRole] = useState("student");
  const [myId, setMyId] = useState(null);
  const [doubts, setDoubts] = useState([]);
  const [selectedDoubt, setSelectedDoubt] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  
  const [showCreate, setShowCreate] = useState(false);
  const [newDoubt, setNewDoubt] = useState({ title: "", description: "" });
  const [liveLinkInput, setLiveLinkInput] = useState("");
  const [showLiveInput, setShowLiveInput] = useState(false);

  const chatEndRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const dec = jwtDecode(token);
      setRole(dec.role || "student");
      setMyId(dec.sub); // assuming sub is user id, adjust if backend returns 'id' specifically, wait, best fetch /me?
    }
    loadDoubts();
  }, []);

  // Correctly resolve current user ID to facilitate message alignment
  useEffect(() => {
     api.get("/users/me").then(res => setMyId(res.data.id)).catch(()=>{});
  }, []);

  const loadDoubts = async () => {
    try {
      const res = await api.get("/doubts");
      setDoubts(res.data);
      if (selectedDoubt) {
         // Refresh the active one to check for live class link updates
         const updated = res.data.find(d => d.id === selectedDoubt.id);
         if (updated) setSelectedDoubt(updated);
      }
    } catch (err) {
      toast.error("Connection Error");
    } finally {
      setLoading(false);
    }
  };

  // Auto-polling for live chat updates every 4 seconds
  useEffect(() => {
     const intv = setInterval(() => {
         if (selectedDoubt) loadMessages(selectedDoubt.id);
         loadDoubts();
     }, 4000);
     return () => clearInterval(intv);
  }, [selectedDoubt]);

  const loadMessages = async (id) => {
    try {
      const res = await api.get(`/doubts/${id}/messages`);
      setMessages(res.data);
    } catch {}
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const selectDoubt = (d) => {
    setSelectedDoubt(d);
    loadMessages(d.id);
  };

  const handleCreateDoubt = async (e) => {
    e.preventDefault();
    try {
      await api.post("/doubts", newDoubt);
      toast.success("Doubt transmitted successfully");
      setShowCreate(false);
      setNewDoubt({ title: "", description: "" });
      loadDoubts();
    } catch { toast.error("Failed to submit doubt"); }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    try {
      await api.post(`/doubts/${selectedDoubt.id}/messages`, { content: newMessage });
      setNewMessage("");
      loadMessages(selectedDoubt.id);
    } catch {}
  };

  const broadcastLiveLink = async () => {
    if (!liveLinkInput.trim()) return;
    try {
      await api.put(`/doubts/${selectedDoubt.id}/live-link`, { link: liveLinkInput });
      toast.success("Live Session Initiated!");
      setShowLiveInput(false);
      setLiveLinkInput("");
      loadDoubts();
      loadMessages(selectedDoubt.id);
    } catch { toast.error("Broadcasting failed"); }
  };

  return (
    <div className={isEmbedded ? "w-full" : "flex min-h-screen bg-slate-50 dark:bg-slate-950"}>
            <main className={`flex-1 min-w-0 ${isEmbedded ? "" : "p-6"}`}>
        <div className={`flex overflow-hidden border border-slate-100 dark:border-slate-900 rounded-[2rem] shadow-2xl font-['Inter',sans-serif] ${isEmbedded ? "h-[600px] bg-white dark:bg-slate-950" : "h-[calc(100vh-90px)] bg-slate-50 dark:bg-slate-950"}`}>
       
       {/* LEFT COLUMN: Sidebar for doubts */}
       <div className="w-80 lg:w-96 border-r border-slate-200 dark:border-slate-800 flex flex-col bg-white dark:bg-slate-900 shrink-0">
          <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-900/50">
             <h2 className="font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
                <HelpCircle size={20} className="text-blue-600" /> Support Desk
             </h2>
             {role === "student" && (
                <button 
                   onClick={() => setShowCreate(true)}
                   className="w-8 h-8 bg-blue-600 hover:bg-blue-700 text-white rounded-full flex items-center justify-center shadow-lg transition-all"
                >
                   <Plus size={18} />
                </button>
             )}
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar">
             {loading ? (
                <div className="p-8 text-center animate-pulse text-slate-400">Loading queue...</div>
             ) : doubts.length === 0 ? (
                <div className="p-10 text-center flex flex-col items-center justify-center h-full text-slate-400">
                   <MessageSquare size={32} className="opacity-20 mb-3" />
                   <p className="text-xs font-bold">No active inquiries</p>
                </div>
             ) : (
                <div className="divide-y divide-slate-100 dark:divide-slate-800">
                   {doubts.map(d => (
                      <div 
                         key={d.id}
                         onClick={() => selectDoubt(d)}
                         className={`p-4 cursor-pointer transition-all hover:bg-slate-50 dark:hover:bg-slate-800/50 ${selectedDoubt?.id === d.id ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-600' : 'border-l-4 border-transparent'}`}
                      >
                         <div className="flex justify-between items-start mb-1">
                            <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${d.status === 'open' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-slate-100 text-slate-600'}`}>
                               {d.status}
                            </span>
                            <span className="text-[10px] font-mono text-slate-400">{new Date(d.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                         </div>
                         <h4 className="font-bold text-slate-900 dark:text-white text-sm line-clamp-1">{d.title}</h4>
                         <p className="text-xs text-slate-500 mt-1 truncate">{role === 'student' ? 'Direct message' : `From: ${d.student_email}`}</p>
                         {d.live_class_link && (
                             <div className="mt-2 flex items-center gap-1 text-[10px] text-rose-600 font-black animate-pulse">
                                 <Video size={12}/> LIVE NOW
                             </div>
                         )}
                      </div>
                   ))}
                </div>
             )}
          </div>
       </div>

       {/* RIGHT COLUMN: The Active Workspace */}
       <div className="flex-1 flex flex-col bg-[#fdfdfd] dark:bg-[#0d1017] relative">
          {selectedDoubt ? (
             <>
                {/* Chat Header */}
                <div className="h-16 border-b dark:border-slate-800 bg-white dark:bg-slate-900 px-6 flex items-center justify-between shrink-0">
                   <div>
                      <h3 className="font-extrabold text-slate-900 dark:text-white leading-tight text-sm sm:text-base">{selectedDoubt.title}</h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Active Case #{selectedDoubt.id}</p>
                   </div>
                   {role !== "student" && (
                      <button 
                         onClick={() => setShowLiveInput(!showLiveInput)}
                         className="flex items-center gap-2 bg-rose-50 hover:bg-rose-100 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all border border-rose-200 dark:border-rose-900/50 shadow-sm"
                      >
                         <Video size={14} /> 
                         {selectedDoubt.live_class_link ? "Update Live Link" : "Go Live"}
                      </button>
                   )}
                </div>

                {/* Teacher Live Link Builder */}
                {showLiveInput && (
                   <div className="bg-rose-50 dark:bg-rose-900/10 p-4 border-b border-rose-100 dark:border-rose-900/30 flex gap-2 items-center animate-in slide-in-from-top duration-200">
                      <input 
                         placeholder="Enter Zoom / Google Meet Link..." 
                         value={liveLinkInput} onChange={e=>setLiveLinkInput(e.target.value)}
                         className="flex-1 bg-white dark:bg-slate-900 border border-rose-200 rounded-xl px-4 py-2 text-sm outline-none text-slate-900 dark:text-white"
                      />
                      <button onClick={broadcastLiveLink} className="bg-rose-600 hover:bg-rose-700 text-white font-bold px-4 py-2 rounded-xl text-sm flex items-center gap-2">
                         Broadcast <ArrowRight size={14}/>
                      </button>
                   </div>
                )}

                {/* THE LIVE ANNOUNCEMENT BANNER - VISIBLE TO EVERYONE IF ACTIVE */}
                {selectedDoubt.live_class_link && (
                    <div className="mx-4 mt-4 p-5 bg-gradient-to-r from-rose-600 to-orange-600 text-white rounded-2xl shadow-xl flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                           <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center animate-pulse">
                               <Video size={24} className="text-white fill-current"/>
                           </div>
                           <div>
                               <h4 className="font-black uppercase tracking-wider text-xs">Synchronous Resolution Available</h4>
                               <p className="text-sm font-medium opacity-90">Instructor has launched a real-time collaboration bridge.</p>
                           </div>
                        </div>
                        <a 
                           href={selectedDoubt.live_class_link.startsWith('http') ? selectedDoubt.live_class_link : `https://${selectedDoubt.live_class_link}`} 
                           target="_blank" rel="noreferrer"
                           className="px-6 py-3 bg-white text-rose-600 rounded-xl font-black text-sm hover:shadow-lg hover:scale-105 transition-all"
                        >
                           Join Live Visual Room
                        </a>
                    </div>
                )}

                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar">
                   {messages.map(msg => {
                      const isMine = msg.sender_id === myId || (msg.sender_role === role && role === 'teacher'); 
                      // Fallback heuristic, preferably check explicit user object ID from backend /me response
                      
                      return (
                         <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                               isMine 
                               ? 'bg-blue-600 text-white rounded-br-sm shadow-md' 
                               : msg.sender_role === 'teacher' 
                                  ? 'bg-emerald-50 dark:bg-emerald-900/20 text-slate-800 dark:text-slate-200 border border-emerald-100 dark:border-emerald-900/30 rounded-bl-sm'
                                  : 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 rounded-bl-sm'
                            }`}>
                               <div className="text-[9px] font-black uppercase tracking-widest opacity-60 mb-1 flex items-center gap-1">
                                  {msg.sender_role === 'teacher' && <CheckCircle2 size={10}/>}
                                  {msg.sender_role}
                               </div>
                               <div className="whitespace-pre-wrap font-medium">{msg.content}</div>
                            </div>
                         </div>
                      );
                   })}
                   <div ref={chatEndRef} />
                </div>

                {/* Message Input Bar */}
                <form onSubmit={sendMessage} className="h-20 bg-white dark:bg-slate-900 border-t dark:border-slate-800 px-6 flex items-center gap-4 shrink-0">
                   <input 
                      value={newMessage}
                      onChange={e => setNewMessage(e.target.value)}
                      placeholder="Collaborate via prompt..."
                      className="flex-1 bg-slate-50 dark:bg-slate-800 rounded-2xl px-5 py-3 text-sm outline-none focus:ring-2 ring-blue-500/30 dark:text-white border border-transparent focus:border-blue-500"
                   />
                   <button type="submit" className="h-12 w-12 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl flex items-center justify-center transition-all shadow-lg shadow-blue-500/20">
                      <Send size={18} className="-rotate-45 ml-1" />
                   </button>
                </form>
             </>
          ) : (
             <div className="flex-1 flex flex-col items-center justify-center text-slate-300 dark:text-slate-700">
                <MessageSquare size={80} className="stroke-1 opacity-20 mb-4" />
                <h3 className="text-lg font-bold">Secure Collaboration Hub</h3>
                <p className="text-sm">Select active ticket from navigation column to initiate logic clearance.</p>
             </div>
          )}
       </div>

       {/* CREATE DOUBT OVERLAY (STUDENT ONLY) */}
       {showCreate && (
          <div className="fixed inset-0 z-[60] bg-slate-900/80 flex items-center justify-center p-4 backdrop-blur-md">
             <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="p-6 border-b dark:border-slate-800 flex justify-between items-center">
                   <h3 className="font-black dark:text-white flex items-center gap-2"><HelpCircle size={20} className="text-blue-600"/> Formalize Query</h3>
                   <button onClick={()=>setShowCreate(false)} className="text-slate-400 hover:text-slate-600"><X size={20}/></button>
                </div>
                <form onSubmit={handleCreateDoubt} className="p-6 space-y-5">
                   <div>
                      <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Problem Summary</label>
                      <input 
                         required placeholder="E.g., Calculus Derivative Chain Rule confusion"
                         className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-800 border-0 outline-none focus:ring-2 ring-blue-500 dark:text-white font-bold"
                         value={newDoubt.title} onChange={e=>setNewDoubt({...newDoubt, title: e.target.value})}
                      />
                   </div>
                   <div>
                      <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Deep Description</label>
                      <textarea 
                         required rows={4} placeholder="Explicitly detail where you are stalled..."
                         className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-800 border-0 outline-none focus:ring-2 ring-blue-500 dark:text-white font-medium text-sm"
                         value={newDoubt.description} onChange={e=>setNewDoubt({...newDoubt, description: e.target.value})}
                      />
                   </div>
                   <button type="submit" className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-black rounded-2xl shadow-xl shadow-blue-600/30 transition-all">
                      Establish Connection
                   </button>
                </form>
             </div>
          </div>
       )}
        </div>
      </main>
    </div>
  );
}
